import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { CcfBox, CcfTypography } from '@nice-ccf/ui-controls';
import {
  ProcessAutomationConfig,
  ProcessAutomationConversationTurn,
  ProcessAutomationInvocation,
  ProcessAutomationOutput,
  ProcessAutomationWorkflow,
  cloneProcessAutomationTemplate,
  processAutomationTemplates,
} from '@nice-ccf/ui-components';
import styles from './simulator-v2-process-automation-tab.module.scss';

/* eslint-disable-next-line */
export interface SimulatorV2ProcessAutomationTabProps {
  updatedContact: any;
  setUpdatedContact: any;
}

const INVOCATION_LABELS: Partial<Record<ProcessAutomationInvocation, string>> =
  {
    [ProcessAutomationInvocation.MANUAL]: 'Manual (Task Assistant only)',
    [ProcessAutomationInvocation.AUTO_RUN]: 'Rule - Auto run',
  };
const INVOCATION_OPTIONS = [
  ProcessAutomationInvocation.MANUAL,
  ProcessAutomationInvocation.AUTO_RUN,
];

let idCounter = 0;
/**
 * Generates a unique id with a prefix
 * @param prefix - string
 * @example nextId('step')
 * @returns string
 */
const nextId = (prefix: string) => `${prefix}-${Date.now()}-${idCounter++}`;

interface FormField {
  id: string;
  label: string;
  type: string;
  choices?: { title: string; value: string }[];
}

/**
 * Lists the inputs of a workflow's form cards, so a conversation turn can pick
 * the fields it fills by their readable label
 * @param workflow - ProcessAutomationWorkflow
 * @example collectFormFields(workflow)
 * @returns FormField[]
 */
const collectFormFields = (workflow: ProcessAutomationWorkflow) => {
  const fields: FormField[] = [];
  /**
   * Visits a card node and its children, collecting Input elements
   * @param node - card node
   * @example walk(card)
   */
  const walk = (node: any) => {
    if (!node || typeof node !== 'object') return;
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (typeof node.type === 'string' && node.type.startsWith('Input.') && node.id) {
      fields.push({
        id: node.id,
        label: String(node.label ?? node.id).replace('*', '').trim(),
        type: node.type,
        choices: node.choices,
      });
    }
    Object.values(node).forEach(walk);
  };
  workflow.steps?.forEach((step) => walk(step.card));
  return fields;
};


/**
 * Simulator-v2 tab where a demo builder configures Process Automation (Task
 * Assistant) workflows for a Digital Chat journey. Mirrors the Copilot Profile
 * "Task Assistant" tab. Everything is stored on `updatedContact.processAutomation`.
 * @param props - SimulatorV2ProcessAutomationTabProps
 * @example <SimulatorV2ProcessAutomationTab />
 * @returns Process Automation tab
 */
export function SimulatorV2ProcessAutomationTab({
  updatedContact,
  setUpdatedContact,
}: SimulatorV2ProcessAutomationTabProps) {
  const [editingWorkflowId, setEditingWorkflowId] = useState<string>();
  // Fields added to a question that don't have a value yet, by turn id
  const [addedFields, setAddedFields] = useState<Record<string, string[]>>({});
  const config: ProcessAutomationConfig = updatedContact?.processAutomation ?? {
    enabled: false,
    workflows: [],
  };
  const isCopilotOn = updatedContact?.copilotAssist === 'Yes';

  /**
   * Writes the Process Automation config to the journey
   * @param next - ProcessAutomationConfig
   * @example updateConfig(nextConfig)
   */
  const updateConfig = (next: ProcessAutomationConfig) => {
    setUpdatedContact((prev: any) => ({ ...prev, processAutomation: next }));
  };

  // The task library always starts with the ready-made tasks (Book an Iceland
  // Trip), so an enabled journey never shows an empty library
  useEffect(() => {
    if (!config.enabled || config.workflows.length > 0) return;
    setUpdatedContact((prev: any) => {
      const current: ProcessAutomationConfig | undefined =
        prev?.processAutomation;
      if (!current?.enabled || current.workflows.length > 0) return prev;
      return {
        ...prev,
        processAutomation: {
          ...current,
          workflows: processAutomationTemplates
            .map((template) => cloneProcessAutomationTemplate(template.id))
            .filter(
              (workflow): workflow is ProcessAutomationWorkflow => !!workflow
            ),
        },
      };
    });
  }, [config.enabled, config.workflows.length]);

  /**
   * Updates one workflow of the journey
   * @param workflowId - string
   * @param changes - Partial
   * @example updateWorkflow(id, changes)
   */
  const updateWorkflow = (
    workflowId: string,
    changes: Partial<ProcessAutomationWorkflow>
  ) => {
    setUpdatedContact((prev: any) => {
      const current: ProcessAutomationConfig = prev?.processAutomation ?? {
        enabled: true,
        workflows: [],
      };
      return {
        ...prev,
        processAutomation: {
          ...current,
          workflows: current.workflows.map((workflow) =>
            workflow.id === workflowId ? { ...workflow, ...changes } : workflow
          ),
        },
      };
    });
  };

  /**
   * Renders the Copilot-led conversation editor
   * @param workflow - ProcessAutomationWorkflow
   * @example renderConversationEditor(workflow)
   * @returns editor
   */
  const renderConversationEditor = (workflow: ProcessAutomationWorkflow) => {
    const turns = workflow.conversation ?? [];
    /**
     * Writes the conversation to the workflow
     * @param next - ProcessAutomationConversationTurn[]
     * @example setTurns(turns)
     */
    const setTurns = (next: ProcessAutomationConversationTurn[]) =>
      updateWorkflow(workflow.id, { conversation: next });
    /**
     * Updates one conversation turn
     * @param index - number
     * @param changes - Partial
     * @example updateTurn(0, changes)
     */
    const updateTurn = (
      index: number,
      changes: Partial<ProcessAutomationConversationTurn>
    ) =>
      setTurns(
        turns.map((turn, i) => (i === index ? { ...turn, ...changes } : turn))
      );

    const formFields = collectFormFields(workflow);

    /**
     * Renders the form field's input, matching its type, labelled by its name
     * @param field - FormField
     * @param value - string
     * @param onChange - value change handler
     * @example renderFieldInput(field, '2', setValue)
     * @returns field input
     */
    const renderFieldInput = (
      field: FormField,
      value: string,
      onChange: (value: string) => void
    ) => {
      if (field.type === 'Input.ChoiceSet' && field.choices?.length) {
        return (
          <TextField
            key={field.id}
            select
            size="small"
            label={field.label}
            value={value}
            onChange={(event) => onChange(event.target.value)}
          >
            <MenuItem value="">
              <em>Not filled</em>
            </MenuItem>
            {field.choices.map((choice) => (
              <MenuItem key={choice.value} value={choice.value}>
                {choice.title}
              </MenuItem>
            ))}
          </TextField>
        );
      }
      const inputTypes: Record<string, string> = {
        'Input.Date': 'date',
        'Input.Number': 'number',
      };
      return (
        <TextField
          key={field.id}
          size="small"
          label={field.label}
          type={inputTypes[field.type] ?? 'text'}
          InputLabelProps={{ shrink: true }}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    };

    return (
      <Box className={styles.outputEditor}>
        <CcfTypography className={styles.subHeaderText}>
          Copilot conversation
        </CcfTypography>
        <CcfTypography className={styles.descriptionText}>
          When the agent clicks Run, Copilot asks each question in the chat in
          order. Enter what the customer&apos;s answer fills in the form; leave
          a field empty if this question doesn&apos;t fill it. Turn on
          &quot;Customer goes off-script&quot; to pause the task until the
          agent clicks Resume.
        </CcfTypography>
        {turns.map((turn, index) => {
          /**
           * Sets one form field filled by this question; empty clears it
           * @param fieldId - string
           * @param value - string
           * @example setFieldValue('travelers', '2')
           */
          const setFieldValue = (fieldId: string, value: string) => {
            const fills = { ...(turn.fills ?? {}) };
            if (value) fills[fieldId] = value;
            else {
              delete fills[fieldId];
              // Keep the emptied field on screen while the builder edits it
              showField(fieldId);
            }
            updateTurn(index, { fills });
          };
          /**
           * Shows a form field on this question, without a value yet
           * @param fieldId - string
           * @example showField('travelers')
           */
          const showField = (fieldId: string) =>
            setAddedFields((prev) => ({
              ...prev,
              [turn.id]: [
                ...(prev[turn.id] ?? []).filter((id) => id !== fieldId),
                fieldId,
              ],
            }));
          /**
           * Removes a form field (and its value) from this question
           * @param fieldId - string
           * @example removeField('travelers')
           */
          const removeField = (fieldId: string) => {
            setAddedFields((prev) => ({
              ...prev,
              [turn.id]: (prev[turn.id] ?? []).filter((id) => id !== fieldId),
            }));
            const fills = { ...(turn.fills ?? {}) };
            delete fills[fieldId];
            updateTurn(index, { fills });
          };
          /**
           * Whether this question shows the form field
           * @param field - FormField
           * @example isShown(field)
           * @returns boolean
           */
          const isShown = (field: FormField) =>
            field.id in (turn.fills ?? {}) ||
            !!addedFields[turn.id]?.includes(field.id);
          const shownFields = formFields.filter(isShown);
          const hiddenFields = formFields.filter((field) => !isShown(field));
          return (
            <Box key={turn.id} className={styles.turnCard}>
              <Box className={styles.turnHeader}>
                <CcfTypography className={styles.turnTitle}>
                  Question {index + 1}
                </CcfTypography>
                {turn.needsAttention && (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label="Needs agent"
                  />
                )}
                <Box className={styles.turnActions}>
                  <Tooltip title="Move up">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Move up"
                        disabled={index === 0}
                        onClick={() => {
                          const next = [...turns];
                          [next[index - 1], next[index]] = [
                            next[index],
                            next[index - 1],
                          ];
                          setTurns(next);
                        }}
                      >
                        <KeyboardArrowUpIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Move down"
                        disabled={index === turns.length - 1}
                        onClick={() => {
                          const next = [...turns];
                          [next[index + 1], next[index]] = [
                            next[index],
                            next[index + 1],
                          ];
                          setTurns(next);
                        }}
                      >
                        <KeyboardArrowDownIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete question">
                    <IconButton
                      size="small"
                      aria-label="Delete question"
                      onClick={() =>
                        setTurns(turns.filter((_, i) => i !== index))
                      }
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Box className={styles.turnFields}>
                <TextField
                  size="small"
                  label="Copilot asks"
                  placeholder="e.g. Which dates would you like to travel?"
                  multiline
                  minRows={2}
                  value={turn.copilotMessage}
                  onChange={(event) =>
                    updateTurn(index, { copilotMessage: event.target.value })
                  }
                />
                <TextField
                  size="small"
                  label="Customer answers"
                  placeholder="e.g. June 15th to June 22nd"
                  multiline
                  minRows={2}
                  value={turn.customerReply}
                  onChange={(event) =>
                    updateTurn(index, { customerReply: event.target.value })
                  }
                />
              </Box>

              <CcfTypography className={styles.fillsLabel}>
                Fills in the form
              </CcfTypography>
              {shownFields.length === 0 ? (
                <CcfTypography className={styles.descriptionText}>
                  This question doesn&apos;t fill the form (for example, a
                  confirmation).
                </CcfTypography>
              ) : (
                <Box className={styles.fillGrid}>
                  {shownFields.map((field) => (
                    <Box key={field.id} className={styles.fillItem}>
                      {renderFieldInput(
                        field,
                        turn.fills?.[field.id] ?? '',
                        (value) => setFieldValue(field.id, value)
                      )}
                      <Tooltip title={`Remove ${field.label}`}>
                        <IconButton
                          size="small"
                          aria-label={`Remove ${field.label}`}
                          onClick={() => removeField(field.id)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              )}
              {hiddenFields.length > 0 && (
                <Box className={styles.addFieldChips}>
                  {hiddenFields.map((field) => (
                    <Chip
                      key={field.id}
                      size="small"
                      variant="outlined"
                      icon={<AddIcon />}
                      label={field.label}
                      onClick={() => showField(field.id)}
                    />
                  ))}
                </Box>
              )}
              <Box className={styles.turnFooter}>
                <FormControlLabel
                  label="Customer goes off-script (pause until the agent resumes)"
                  control={
                    <Checkbox
                      size="small"
                      checked={!!turn.needsAttention}
                      onChange={(event) =>
                        updateTurn(index, {
                          needsAttention: event.target.checked,
                        })
                      }
                    />
                  }
                />
              </Box>
            </Box>
          );
        })}
        <Button
          size="small"
          startIcon={<AddIcon />}
          className={styles.alignStart}
          onClick={() =>
            setTurns([
              ...turns,
              {
                id: nextId('turn'),
                copilotMessage: '',
                customerReply: '',
                fills: {},
              },
            ])
          }
        >
          Add question
        </Button>
      </Box>
    );
  };

  /**
   * Renders the editable output of a completed task
   * @param workflow - ProcessAutomationWorkflow
   * @example renderOutputEditor(workflow)
   * @returns output editor
   */
  const renderOutputEditor = (workflow: ProcessAutomationWorkflow) => {
    const output: ProcessAutomationOutput = workflow.successOutput ?? {
      summary: '',
    };
    /**
     * Writes the edited output to the workflow
     * @param next - ProcessAutomationOutput
     * @example setOutput(output)
     */
    const setOutput = (next: ProcessAutomationOutput) =>
      updateWorkflow(workflow.id, { successOutput: next });

    return (
      <Box className={styles.outputEditor}>
        <CcfTypography className={styles.subHeaderText}>Completed</CcfTypography>
        <TextField
          size="small"
          label="Agent summary"
          multiline
          minRows={2}
          value={output.summary}
          onChange={(event) =>
            setOutput({ ...output, summary: event.target.value })
          }
        />
        <TextField
          size="small"
          label="Message for the customer"
          helperText="The agent can edit it, insert it in the reply or send it"
          multiline
          minRows={2}
          value={output.customerMessage ?? ''}
          onChange={(event) =>
            setOutput({ ...output, customerMessage: event.target.value })
          }
        />
      </Box>
    );
  };

  /**
   * Renders the detail editor of a workflow
   * @param workflow - ProcessAutomationWorkflow
   * @example renderWorkflowEditor(workflow)
   * @returns editor
   */
  const renderWorkflowEditor = (workflow: ProcessAutomationWorkflow) => {
    return (
      <Box className={styles.workflowEditor}>
        <Box className={styles.fieldGrid}>
          <TextField
            size="small"
            label="Task name"
            required
            value={workflow.name}
            error={!workflow.name.trim()}
            helperText={!workflow.name.trim() ? 'Task name is required' : ''}
            onChange={(event) =>
              updateWorkflow(workflow.id, { name: event.target.value })
            }
          />
          <TextField
            size="small"
            select
            label="Recommendation"
            value={workflow.invocation}
            onChange={(event) =>
              updateWorkflow(workflow.id, {
                invocation: event.target.value as ProcessAutomationInvocation,
              })
            }
          >
            {INVOCATION_OPTIONS.map((value) => (
              <MenuItem key={value} value={value}>
                {INVOCATION_LABELS[value]}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <TextField
          size="small"
          label="Task description"
          multiline
          required
          value={workflow.description}
          error={!workflow.description.trim()}
          helperText={
            !workflow.description.trim()
              ? 'Description is required'
              : 'Shown under the task name in the Task Assistant'
          }
          onChange={(event) =>
            updateWorkflow(workflow.id, { description: event.target.value })
          }
        />
        {renderConversationEditor(workflow)}
        {renderOutputEditor(workflow)}
      </Box>
    );
  };

  return (
    <div className={styles.container}>
      <CcfBox className={styles.section}>
        <CcfTypography className={styles.headerText}>
          Process Automation
        </CcfTypography>
        <CcfTypography className={styles.descriptionText}>
          Adds the Task Assistant to Copilot for this chat journey. Agents open
          it with the ✦ button next to the Copilot bar (or by typing /) and run
          tasks, where Copilot collects the answers in the chat.
        </CcfTypography>
        {!isCopilotOn && (
          <Alert severity="warning">
            Process Automation runs inside Copilot. Set Copilot Assist to
            &quot;Yes&quot; in the Copilot tab to show it to the agent.
          </Alert>
        )}
        <FormControlLabel
          label="Enable Process Automation for this journey"
          control={
            <Switch
              color="primary"
              checked={config.enabled}
              onChange={(event) =>
                updateConfig({ ...config, enabled: event.target.checked })
              }
            />
          }
        />

        {config.enabled && (
          <>
            <Box className={styles.libraryHeader}>
              <CcfTypography className={styles.subHeaderText}>
                Task library
              </CcfTypography>
            </Box>

            {config.workflows.map((workflow) => {
              const isEditing = editingWorkflowId === workflow.id;
              return (
                <Box key={workflow.id} className={styles.workflowRow}>
                  <Box className={styles.workflowRowHeader}>
                    <Box className={styles.workflowRowText}>
                      <CcfTypography className={styles.subHeaderText}>
                        {workflow.name || 'Untitled task'}
                      </CcfTypography>
                      <CcfTypography className={styles.descriptionText}>
                        {workflow.intentName}
                      </CcfTypography>
                    </Box>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        INVOCATION_LABELS[workflow.invocation] ??
                        INVOCATION_LABELS[ProcessAutomationInvocation.MANUAL]
                      }
                    />
                    <Tooltip title={isEditing ? 'Collapse' : 'Edit'}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setEditingWorkflowId(
                            isEditing ? undefined : workflow.id
                          )
                        }
                      >
                        {isEditing ? (
                          <ExpandLessIcon fontSize="small" />
                        ) : (
                          <EditOutlinedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {isEditing && renderWorkflowEditor(workflow)}
                </Box>
              );
            })}
          </>
        )}
      </CcfBox>
    </div>
  );
}

export default SimulatorV2ProcessAutomationTab;
